<template>
    <div class="form-group">
        <div class="input-icon form-floating">
            <input
                :type="type" 
                ref="txtNumberInput"
                :id="`input-${id}`"
                :value="displayValue"
                :readonly="isReadOnly" 
                @input="updateModel" 
                @keypress="onKeyPress" 
                @focus="hasFocus = true"
                @blur="hasFocus = false"
                @keyup.enter="emit('entered')"
                class="form-control"
            />

            <validation-label
                ref="label"
                :elementId="`input-${id}`"
                :text="description"
                :abbreviatedText="abbreviatedDescription"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, PropType, watchEffect } from 'vue';
import ValidationLabel from '@/shared/components/ValidationLabel.vue';
import { generateUUID } from '@/guiide/ts/utilities';

const props = defineProps({
    modelValue: {
        type: [String] as PropType<string>,
        default: ""
    },
    description: { type: String, default: "" },
    abbreviatedDescription: { type: String, default: "" },
    optional: { type: Boolean, default: false },
    isReadOnly: { type: Boolean, default: false },
    regex: { type: RegExp, default: null },
    keyPressRegex: { type: RegExp, default: null },
    validateOnChange: { type: Boolean, default: false },
    type: { type: String, default: "text" }
});

const emit = defineEmits(['update:modelValue', 'entered']);

const id = generateUUID();
const txtNumberInput = ref(null);
const label = ref(null);
const displayValue = ref(props.modelValue?.toString() ?? '');
const hasFocus = ref(false);

const updateModel = (event: Event) => {
    const value = (event.target as HTMLInputElement).value;

    if (props.validateOnChange) {
        isValidInternal(value);
    }

    emit('update:modelValue', value);
};

watchEffect(() => {
    if (props.modelValue === null) {
        displayValue.value = "";
        return;
    }

    if (hasFocus.value) {
        displayValue.value = props.modelValue.toString();
    } else {
        displayValue.value = props.modelValue.toLocaleString();
    }
});

const onKeyPress = (event: KeyboardEvent) => {
    const keyValue = event.key;
    if (props.keyPressRegex && !keyValue.match(props.keyPressRegex)) {
        event.preventDefault();
    }
};

const isValid = (): boolean => {
    return isValidInternal(props.modelValue);
}

function isValidInternal(valueToValidate: string): boolean {
    if (valueToValidate === "") {
        if (!props.optional) {
            label.value.warn("required");
            return false;
        }
        return true;
    }
    
    if (props.regex && !props.regex.test(valueToValidate)) {
        label.value.warn("invalid");
        return false;
    }

    label.value.hideAnyWarnings();
    return true;
}

const hideAnyWarnings = () => {
    label.value.hideAnyWarnings();
}

function takeFocus() {
    txtNumberInput.value.focus();
}

defineExpose({ isValid, hideAnyWarnings, takeFocus });

</script>

<style scoped>
.input-icon {
  position: relative;
}

.input-icon > i {
  position: absolute;
  display: block;
  transform: translate(0, -22%);
  top: 53%;
  left: 7px;
  pointer-events: none;
  width: 25px;
  text-align: center;
  font-style: normal;
}

.input-icon > input[type="text"]:focus + i {
  display: block;
}

.input-icon > input {
  padding-left: 13px;
  padding-right: 0;
}

.form-floating > label {
  background-color: white;
  height: 96%;
  width: 99%;
}

.form-floating > .form-control:focus ~ label,
.form-floating > .form-control:not(:placeholder-shown) ~ label,
.form-floating > .form-select ~ label {
  background-color: transparent;
  height: 96%;
}

.form-floating > label {
  top: 1px;
  left: 1px;
  padding: 0.8rem 0.75rem;
  border-radius: 4px;
}
</style>
